import { motion } from "framer-motion";
import { useLang } from "../../context/LanguageContext";
import { factories } from "../../data/content";

const ease = [0.22, 1, 0.36, 1];

const IndustrialServices = () => {
  const { isAr, t } = useLang();
  return (
    <section
      id="services"
      className="relative bg-bg-ivory py-[120px] md:py-[180px]"
      data-testid="services-section"
    >
      <div className="max-w-[1760px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex justify-between items-center text-brand-green/65 mb-16">
          <span className="page-meta">— 10</span>
          <span className="page-meta">{t("خدماتنا الصناعية", "Our Industrial Services")}</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease }}
          className="mb-16 max-w-3xl"
        >
          <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-brand-green/55 mb-5`}>
            {t("[ خدماتنا الصناعية ]", "[ INDUSTRIAL SERVICES ]")}
          </div>
          <h3
            className={`${isAr ? "arabic-title" : "serif-title"} text-brand-green mb-8`}
            style={{ fontSize: isAr ? "clamp(40px, 5vw, 76px)" : "clamp(48px, 5.6vw, 100px)" }}
          >
            {isAr ? "خدماتنا الصناعية" : (<>Our <em className="font-light">Industrial Services</em></>)}
          </h3>
          {isAr ? (
            <p className="arabic-title text-brand-green/75 max-w-xl" dir="rtl" style={{ fontSize: 16, lineHeight: 1.95, fontWeight: 400 }}>
              تقدّم BigBrand منظومة خدمات صناعية متكاملة عبر مصنعَين متخصصَين صُمّمت لتلبية متطلبات المشاريع الكبرى بكفاءة عالية ومرونة كاملة.
            </p>
          ) : (
            <p className="text-brand-green/75 max-w-xl" style={{ fontSize: 16, lineHeight: 1.8 }}>
              BigBrand delivers an integrated industrial service system through two specialized factories — designed to meet the demands of major projects with high efficiency and complete flexibility.
            </p>
          )}
        </motion.div>

        <div className="editorial-rule mb-12" />

        <div className="space-y-12 md:space-y-20">
          {factories.map((f, idx) => (
            <motion.div
              key={f.code}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.1, ease }}
              className="grid grid-cols-12 gap-x-10 gap-y-8 items-stretch"
            >
              <div className={`col-span-12 md:col-span-6 ${idx % 2 === 1 ? "md:order-2" : ""}`}>
                <div className="relative overflow-hidden group h-full min-h-[420px]">
                  <img
                    src={f.image}
                    alt={isAr ? f.ar_title : f.en_title}
                    className="w-full h-full object-cover transition-transform duration-[1600ms] group-hover:scale-105"
                    style={{ minHeight: 420 }}
                  />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(23,63,53,0.1), rgba(23,63,53,0.3))" }} />
                  <div className="absolute top-5 left-5 right-5 flex justify-between text-bg-ivory">
                    <span className="page-meta">— Factory {f.code}</span>
                    <span className="label-meta">[ {f.code} / 02 ]</span>
                  </div>
                </div>
              </div>

              <div className={`col-span-12 md:col-span-6 bg-bg-cream/40 p-8 md:p-12 ${idx % 2 === 1 ? "md:order-1" : ""}`}>
                <div className="flex items-start justify-between mb-8">
                  <div
                    className="font-serif-display text-brand-green"
                    style={{ fontSize: 96, lineHeight: 0.85, opacity: 0.85 }}
                  >
                    {f.code}
                  </div>
                  <div className="text-end">
                    <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-brand-green/55`}>
                      {t("[ مصنع ]", "[ FACTORY ]")}
                    </div>
                  </div>
                </div>

                <h4
                  className={`${isAr ? "arabic-title" : "serif-title"} text-brand-green mb-3`}
                  style={{ fontSize: isAr ? 32 : "clamp(28px, 3vw, 44px)" }}
                >
                  {isAr ? f.ar_title : f.en_title}
                </h4>
                <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-brand-green/65 mb-8`}>
                  {isAr ? f.ar_caption : f.en_caption}
                </div>

                <div className="editorial-rule mb-6" />

                <ul className="space-y-1">
                  {f.items.map((item, i) => (
                    <li key={i} className="grid grid-cols-12 gap-3 py-3 border-b border-brand-green/10">
                      <span className={`col-span-2 ${isAr ? "label-meta-ar" : "label-meta"} text-brand-green/55 mt-0.5`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span
                        className={`col-span-10 ${isAr ? "arabic-title" : ""} text-brand-green`}
                        dir={isAr ? "rtl" : "ltr"}
                        style={{ fontSize: 15, fontWeight: isAr ? 500 : 400 }}
                      >
                        {isAr ? item.ar : item.en}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustrialServices;
